import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  addMinistersToRequest,
  removeMinisterFromRequest,
  createRequest,
} from "@/data/credentialReqRepository";
import type { Minister, MinisterAvailability } from "../types";

interface UseMinisterManagementProps {
  requestId?: string;
  isNewRequest: boolean;
  districtId: string | null;
  regionId: string | null;
  year: number;
  userId?: string;
  notes?: string;
  credentialType?: 'MINISTERIAL' | 'INSTITUTION' | 'FUNCTION';
  code?: string;
}

interface UseMinisterManagementReturn {
  // State
  isSubmitting: boolean;
  error: string | null;
  
  // Actions
  addMultipleMinisters: (
    ministerIds: Set<string>,
    availableMinisters: MinisterAvailability[],
    onSuccess: (addedMinisters: Minister[], newRequestId?: string) => void,
    suppressNavigation?: boolean
  ) => Promise<void>;
  removeMinister: (
    ministerId: string,
    onSuccess: () => void
  ) => Promise<void>;
  setError: (error: string | null) => void;
}

/**
 * Hook to manage minister addition and removal operations
 */
export function useMinisterManagement({
  requestId,
  isNewRequest,
  districtId,
  regionId,
  year,
  userId,
  notes,
  credentialType = 'MINISTERIAL',
  code,
}: UseMinisterManagementProps): UseMinisterManagementReturn {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addMultipleMinisters = useCallback(async (
    ministerIds: Set<string>,
    availableMinisters: MinisterAvailability[],
    onSuccess: (addedMinisters: Minister[], newRequestId?: string) => void,
    suppressNavigation: boolean = false
  ) => {
    if (ministerIds.size === 0) {
      setError("Por favor seleccione al menos un ministro");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let currentRequestId = requestId;

      // Auto-create draft for new requests
      if (isNewRequest && !currentRequestId) {
        if (!userId) {
          setError("Falta información de usuario para crear la solicitud");
          return;
        }
        
        // For MINISTERIAL: Require district and region
        // For SEC/FUNCTION: District and region are optional/null
        if ((credentialType === 'MINISTERIAL') && (!districtId || !regionId)) {
          setError("Falta información de distrito y región para crear la solicitud");
          return;
        }

        const newRequest = await createRequest({
          district_id: districtId,
          region_id: regionId,
          year: year,
          status: 'DRAFT',
          created_by: userId,
          notes: notes || null,
          credential_type: credentialType,
          code: code || null,
        });

        if (!newRequest) {
          setError("Error al crear la solicitud");
          return;
        }

        currentRequestId = newRequest.id;
      }

      if (!currentRequestId) return;

      // Add all selected ministers in a single batch operation (performance optimization)
      const ministerIdsArray = Array.from(ministerIds);
      const result = await addMinistersToRequest(currentRequestId, ministerIdsArray);
      
      if (!result.success) {
        setError(`Error al agregar ministros`);
        return;
      }

      const selectedMinisters = availableMinisters.filter(m => ministerIds.has(m.id));

      if (isNewRequest && !suppressNavigation) {
        navigate(`/credential-requests/${currentRequestId}`, { replace: true });
      }

      onSuccess(selectedMinisters, currentRequestId);
    } catch (err) {
      console.error("Error adding ministers:", err);
      setError(err instanceof Error ? err.message : "Error al agregar ministros");
    } finally {
      setIsSubmitting(false);
    }
  }, [requestId, isNewRequest, districtId, regionId, year, userId, notes, credentialType, code, navigate]);

  const removeMinister = useCallback(async (
    ministerId: string,
    onSuccess: () => void
  ) => {
    if (!requestId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const success = await removeMinisterFromRequest(requestId, ministerId);
      if (success) {
        onSuccess();
      } else {
        setError("Error al remover ministro");
      }
    } catch (err) {
      console.error("Error removing minister:", err);
      setError("Error al remover ministro");
    } finally {
      setIsSubmitting(false);
    }
  }, [requestId]);

  return {
    isSubmitting,
    error,
    addMultipleMinisters,
    removeMinister,
    setError,
  };
}
